// TODO(ts-migration): large dialog entangled with untyped .js services
// (CoachDashboardService, TraineeService, PlanSerice) — convert after those are typed.
import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  IconButton,
  Box,
  Typography,
  CircularProgress,
  TextField,
} from "@mui/material";
import {
  Close as CloseIcon,
  Event as EventIcon,
  Cake as CakeIcon,
  AccessTime as ClockIcon,
  Send as SendIcon,
  EditOutlined as EditIcon,
  Check as CheckIcon,
  OpenInNew as OpenInNewIcon,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { RTL } from "../../../cache/CacheRtl";
import { t } from "./coachTokens";
import { getTraineeProfile } from "../../../services/CoachDashboardService";
import { getClientNotes, saveClientNote } from "../../../services/TraineeService";
import { getCurrentPlan } from "../../../services/PlanSerice";
import WeightChart from "./WeightChart";
import WeekPills from "./WeekPills";
import {
  weightTrendStr,
  weightGoalLabel,
  weightGoalMet,
  weightTrendColor,
} from "./weightHelpers";
import { useNutritionDisplayMode } from "../../../contexts/NutritionDisplayContext";
import { POINTS, energyFigure, energyLabel } from "../../nutrition/v2/ui/nutritionUnits";

const SectionHeader = ({ label }) => (
  <Box sx={{ fontSize: 10.5, fontWeight: 700, color: t.t3, textTransform: "uppercase", letterSpacing: ".08em", margin: "16px 0 9px", display: "flex", alignItems: "center", gap: "6px" }}>
    {label}
    <Box sx={{ flex: 1, height: "1px", background: t.border }} />
  </Box>
);

const StatCard = ({ value, valueColor, label, sub, badge }) => (
  <Box sx={{ background: t.bg, borderRadius: t.rs, padding: "10px 12px", border: `1px solid ${t.borderLight}` }}>
    <Box sx={{ fontSize: 16, fontWeight: 800, color: valueColor || t.text, letterSpacing: "-.2px" }}>{value}</Box>
    <Box sx={{ fontSize: 11, color: t.t2, marginTop: "2px" }}>{label}</Box>
    {sub && <Box sx={{ fontSize: 10, color: t.t3, marginTop: "2px" }}>{sub}</Box>}
    {badge && <Box sx={{ marginTop: "4px" }}>{badge}</Box>}
  </Box>
);

const EmptyState = ({ label = "אין נתון עדיין" }) => (
  <Box sx={{
    background: t.bg,
    border: `1px dashed ${t.border}`,
    borderRadius: t.rs,
    padding: "14px",
    textAlign: "center",
    color: t.t3,
    fontSize: 12,
    marginBottom: "14px",
  }}>
    {label}
  </Box>
);

const Pill = ({ ok, label }) => (
  <Box component="span" sx={{
    background: ok ? t.greenLight : t.redLight,
    color: ok ? t.greenText : t.redText,
    fontSize: 10, fontWeight: 700,
    padding: "1px 7px", borderRadius: "10px",
  }}>
    {label}
  </Box>
);

export const weightGoalBadge = (d) => {
  const met = weightGoalMet(d);
  if (met == null) return null;
  return <Pill ok={met} label={met ? "✓ עמד ביעד" : "✗ לא עמד"} />;
};

export const stepsAvgColor = (d) => {
  if (d.stepsAvg == null) return t.t3;
  if (!d.stepsGoal) return t.text;
  return d.stepsAvg >= d.stepsGoal ? t.green : t.text;
};

export const stepsGoalBadge = (d) => {
  if (d.stepsAvg == null || !d.stepsGoal) return null;
  return <Pill ok={d.stepsAvg >= d.stepsGoal} label={d.stepsAvg >= d.stepsGoal ? "✓ עמד" : "✗ לא עמד"} />;
};

export const stepsGoalBadgeLastWeek = (d) => {
  if (d.stepsLastWeek == null || !d.stepsGoal) return null;
  return <Pill ok={d.stepsLastWeek >= d.stepsGoal} label={d.stepsLastWeek >= d.stepsGoal ? "✓ עמד" : "✗ לא עמד"} />;
};

// Met-the-calories-goal check. Mirrors weight semantics: cut → at-or-under,
// gain → at-or-over, anything else → within ±10% (treated as maintenance).
const CALORIES_CUT = new Set(["AGGRESSIVE_CUT", "CUT", "MODERATE_CUT", "SHORT_CUT"]);
const CALORIES_GAIN = new Set(["MODERATE_BULK", "BULK", "AGGRESSIVE_BULK"]);
export const caloriesGoalMet = (value, goal, weightGoal) => {
  if (value == null || !goal) return null;
  if (CALORIES_CUT.has(weightGoal)) return value <= goal;
  if (CALORIES_GAIN.has(weightGoal)) return value >= goal;
  return Math.abs(value - goal) / goal <= 0.1;
};
export const caloriesGoalBadge = (d) => {
  const met = caloriesGoalMet(d.caloriesAvg, d.caloriesGoal, d.weightGoalDirection || d.goal);
  if (met == null) return null;
  return <Pill ok={met} label={met ? "✓ עמד" : "✗ לא עמד"} />;
};
export const caloriesGoalBadgeLastWeek = (d) => {
  const met = caloriesGoalMet(d.caloriesLastWeek, d.caloriesGoal, d.weightGoalDirection || d.goal);
  if (met == null) return null;
  return <Pill ok={met} label={met ? "✓ עמד" : "✗ לא עמד"} />;
};

export const DAY_HEADERS = ["א׳", "ב׳", "ג׳", "ד׳", "ה׳", "ו׳", "ש׳"];

export const fmtDate = (instantOrSecs) => {
  if (instantOrSecs == null) return "–";
  const d = typeof instantOrSecs === "number" ? new Date(instantOrSecs * 1000) : new Date(instantOrSecs);
  if (Number.isNaN(d.getTime())) return "–";
  return `${d.getDate()}/${d.getMonth() + 1}/${d.getFullYear()}`;
};

export const lastWorkoutText = (instantOrSecs) => {
  if (instantOrSecs == null) return "מעולם לא";
  const d = typeof instantOrSecs === "number" ? new Date(instantOrSecs * 1000) : new Date(instantOrSecs);
  if (Number.isNaN(d.getTime())) return "מעולם לא";
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const dayStart = new Date(d);
  dayStart.setHours(0, 0, 0, 0);
  const diff = Math.round((today - dayStart) / 86400000);
  if (diff <= 0) return "היום";
  if (diff === 1) return "אתמול";
  return `לפני ${diff} ימים`;
};

const Chip = ({ icon, label, value, bg, fg, border }) => (
  <Box
    sx={{
      display: "inline-flex",
      alignItems: "center",
      gap: "4px",
      background: bg || t.bg,
      color: fg || t.t2,
      fontSize: "11px",
      fontWeight: 500,
      padding: "4px 10px",
      borderRadius: "20px",
      border: `1px solid ${border || t.border}`,
      whiteSpace: "nowrap",
    }}
  >
    {icon}
    {label}: <strong style={{ color: fg || t.text }}>{value}</strong>
  </Box>
);

const TraineeProfileDialog = ({ open, onClose, traineeId, weekOffset = 0, onSendPush }) => {
  const navigate = useNavigate();
  const nutritionMode = useNutritionDisplayMode();
  // The weekly calorie figures, or points (kcal ÷ 100) for a points company. The met/not-met
  // badges still compare the kcal the server sent, so the two modes can never disagree on them.
  const energyNum = (kcal) => (nutritionMode === POINTS ? energyFigure(kcal, nutritionMode).text : kcal.toLocaleString());
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [notes, setNotes] = useState([]);
  const [noteDraft, setNoteDraft] = useState("");
  const [savingNote, setSavingNote] = useState(false);
  const [savedTick, setSavedTick] = useState(false);
  // Active program, fetched lazily so clicking a workout day can jump straight to
  // that week's workout page (the profile payload alone has no program/block/week ids).
  const [program, setProgram] = useState(null);
  // Local override of the parent weekOffset so the coach can flip between
  // current/last week from inside the dialog. Resets to the prop each open.
  const [internalWeekOffset, setInternalWeekOffset] = useState(weekOffset);

  useEffect(() => {
    if (open) setInternalWeekOffset(weekOffset);
  }, [open, traineeId, weekOffset]);

  useEffect(() => {
    if (!open || traineeId == null) return;
    setLoading(true);
    setDetail(null);
    setNoteDraft("");
    setSavedTick(false);
    Promise.all([getTraineeProfile(traineeId, internalWeekOffset), getClientNotes(traineeId)])
      .then(([d, n]) => {
        setDetail(d.data || null);
        setNotes(n.data?.body || n.data || []);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [open, traineeId, internalWeekOffset]);

  // Load the active program once per open so a day-circle click can resolve the
  // current week's workout route. Independent of weekOffset (program is week-agnostic).
  useEffect(() => {
    if (!open || traineeId == null) { setProgram(null); return; }
    getCurrentPlan(traineeId)
      .then((res) => setProgram(res.data || null))
      .catch(() => setProgram(null));
  }, [open, traineeId]);

  // Jump to the trainee's current (first not-finished) workout week. Falls back to the
  // client page if the program/week can't be resolved, so the click is never a dead end.
  const goToWorkout = () => {
    const blocksAsc = [...(program?.blocks || [])].sort((a, b) => a.index - b.index);
    let target = null;
    for (const block of blocksAsc) {
      const weeks = [...(block.weeks || [])].sort((a, b) => a.index - b.index);
      const wi = weeks.findIndex((w) => !w.finished);
      if (wi !== -1) { target = { blockIndex: block.index, weekIndex: wi }; break; }
    }
    onClose?.();
    if (program?.id && target) {
      navigate(`/app/clients/${traineeId}/${program.id}/week/${target.blockIndex}/${target.weekIndex}`);
    } else {
      navigate(`/app/clients/${traineeId}`);
    }
  };

  // Use the backend's true completed count, not the day-grid length: the grid has one
  // slot per weekday, so two workouts on the same day collapse into one chip and undercount.
  const total = detail?.workoutsDone || 0;
  const goal = detail?.workoutsGoal || 0;
  const metGoal = total >= goal && goal > 0;

  const renewalChip = () => {
    const days = detail?.renewalDays;
    if (days == null) {
      return <Chip icon={<ClockIcon sx={{ fontSize: 12 }} />} label="מנוי" value="–" />;
    }
    const urgent = days <= 7;
    const warning = days > 7 && days <= 14;
    return (
      <Chip
        icon={<ClockIcon sx={{ fontSize: 12 }} />}
        label={days < 0 ? "פג לפני" : "ימים למנוי"}
        value={Math.abs(days)}
        bg={urgent ? t.redLight : warning ? t.bg : t.bg}
        fg={urgent ? t.redText : warning ? t.text : t.t2}
        border={urgent ? "rgba(224,69,69,.2)" : t.border}
      />
    );
  };

  const onSave = async () => {
    if (!noteDraft.trim() || !traineeId) return;
    setSavingNote(true);
    try {
      await saveClientNote({ traineeId, message: noteDraft.trim() });
      const refreshed = await getClientNotes(traineeId);
      setNotes(refreshed.data?.body || refreshed.data || []);
      setNoteDraft("");
      setSavedTick(true);
      setTimeout(() => setSavedTick(false), 1500);
    } finally {
      setSavingNote(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: "16px" } }}>
      <RTL>
        <Box dir="rtl">
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: "12px", padding: "14px 18px", borderBottom: `1px solid ${t.border}` }}>
            <Typography sx={{ fontSize: 17, fontWeight: 800, letterSpacing: "-.3px" }}>
              פרופיל מתאמן
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: "10px", flex: 1, justifyContent: "flex-end" }}>
              <WeekPills value={internalWeekOffset} onChange={setInternalWeekOffset} />
              {traineeId && (
                <IconButton
                  onClick={() => { onClose?.(); navigate(`/app/clients/${traineeId}`); }}
                  size="small"
                  sx={{ color: t.t2, "&:hover": { color: t.text, background: t.bg } }}
                  title="פתח עמוד מלא"
                >
                  <OpenInNewIcon fontSize="small" />
                </IconButton>
              )}
              <IconButton onClick={onClose} size="small" sx={{ color: t.t2, "&:hover": { color: t.text, background: t.bg } }}>
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
          </Box>

          <DialogContent sx={{ padding: "16px 18px", maxHeight: "80vh" }}>
            {loading || !detail ? (
              <Box sx={{ display: "flex", justifyContent: "center", padding: "60px" }}>
                <CircularProgress size={28} />
              </Box>
            ) : (
              <>
                <Box sx={{ paddingBottom: "14px", borderBottom: `1px solid ${t.border}`, marginBottom: "14px" }}>
                  <Box sx={{ fontSize: 16, fontWeight: 700, color: t.text, letterSpacing: "-.2px" }}>{detail.traineeName}</Box>
                  {detail.planName && (
                    <Box sx={{ fontSize: 12, color: t.t2, marginTop: "2px" }}>
                      {detail.planName}
                    </Box>
                  )}
                </Box>

                <Box sx={{ display: "flex", gap: "6px", flexWrap: "wrap", marginBottom: "16px" }}>
                  <Chip icon={<EventIcon sx={{ fontSize: 12 }} />} label="הצטרף" value={fmtDate(detail.joinDate)} />
                  <Chip icon={<CakeIcon sx={{ fontSize: 12 }} />} label="יומולדת" value={fmtDate(detail.birthday)} />
                  {renewalChip()}
                </Box>

                <Box sx={{ fontSize: 10.5, fontWeight: 700, color: t.t3, textTransform: "uppercase", letterSpacing: ".08em", marginBottom: "9px", display: "flex", alignItems: "center", gap: "6px" }}>
                  אימונים
                  <Box sx={{ flex: 1, height: "1px", background: t.border }} />
                </Box>

                <Box sx={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: "4px", marginBottom: "10px" }}>
                  {DAY_HEADERS.map((label, i) => {
                    const state = (detail.weekDays || [])[i];
                    const done = state === "COMPLETED";
                    const inProgress = state === "IN_PROGRESS";
                    // A day with any workout activity is clickable — it jumps the coach to the
                    // client's page to open that workout. Empty days stay inert.
                    const hasWorkout = done || inProgress;
                    // Several workouts can be completed on the same day; the chip shows one ✓,
                    // so badge the extra count to keep the visible chips summing to workoutsDone.
                    const dayCount = (detail.weekDayCounts || [])[i] || 0;
                    const bg = done ? t.greenLight : t.bg;
                    const fg = done ? t.greenText : inProgress ? t.greenText : t.t3;
                    const borderColor = done ? "rgba(1,187,167,.3)" : inProgress ? "rgba(1,187,167,.35)" : t.border;
                    const title = dayCount > 1
                      ? `${dayCount} אימונים הושלמו · פתח אימון`
                      : done
                      ? "אימון הושלם · פתח אימון"
                      : inProgress
                      ? "אימון בתהליך · פתח אימון"
                      : undefined;
                    return (
                      <Box key={label} sx={{ textAlign: "center" }}>
                        <Box sx={{ fontSize: 10, color: t.t3, marginBottom: "3px" }}>{label}</Box>
                        <Box
                          {...(hasWorkout && {
                            role: "button",
                            tabIndex: 0,
                            onClick: goToWorkout,
                            onKeyDown: (e) => {
                              if (e.key === "Enter" || e.key === " ") {
                                e.preventDefault();
                                goToWorkout();
                              }
                            },
                          })}
                          title={title}
                          sx={{
                            position: "relative",
                            width: 28,
                            height: 28,
                            borderRadius: "50%",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            margin: "0 auto",
                            // IN_PROGRESS: half-filled circle (green fill on the leading half)
                            // signalling "started, not finished". COMPLETED stays a full tint.
                            background: inProgress
                              ? `linear-gradient(90deg, ${t.greenLight} 50%, ${t.white} 50%)`
                              : bg,
                            color: fg,
                            border: `1px solid ${borderColor}`,
                            fontSize: 12,
                            fontWeight: 700,
                            cursor: hasWorkout ? "pointer" : "default",
                            transition: "transform .12s, box-shadow .12s",
                            outline: "none",
                            ...(hasWorkout && {
                              "&:hover": { transform: "scale(1.12)", boxShadow: "0 2px 8px rgba(1,187,167,.28)" },
                              "&:focus-visible": { boxShadow: `0 0 0 2px ${t.green}` },
                            }),
                          }}
                        >
                          {done ? "✓" : inProgress ? "" : "–"}
                          {dayCount > 1 && (
                            <Box
                              sx={{
                                position: "absolute",
                                top: -4,
                                insetInlineEnd: -4,
                                minWidth: 14,
                                height: 14,
                                paddingInline: "2px",
                                borderRadius: "7px",
                                background: t.greenText,
                                color: "#fff",
                                fontSize: 9,
                                fontWeight: 700,
                                lineHeight: "14px",
                                textAlign: "center",
                              }}
                            >
                              ×{dayCount}
                            </Box>
                          )}
                        </Box>
                      </Box>
                    );
                  })}
                </Box>

                <Box sx={{ display: "flex", alignItems: "center", gap: "6px", marginBottom: "8px" }}>
                  <Box sx={{ fontSize: 13, fontWeight: 700, color: metGoal ? t.green : t.text }}>
                    {total}/{goal} אימונים
                  </Box>
                  <Box sx={{ fontSize: 11, color: t.t3 }}>· אחרון: {lastWorkoutText(detail.lastWorkoutDate)}</Box>
                  {detail.blockTotal > 0 && (
                    <Box
                      sx={{
                        fontSize: 11,
                        fontWeight: 700,
                        color: t.greenText,
                        background: t.greenLight,
                        padding: "2px 8px",
                        borderRadius: "20px",
                        marginRight: "auto",
                      }}
                    >
                      שבוע {detail.blockWeek}/{detail.blockTotal}
                    </Box>
                  )}
                </Box>

                {/* ⚖️ Weight */}
                <SectionHeader emoji="⚖️" label="משקל — ממוצע שבועי" />
                {detail.weightAvg == null ? (
                  <EmptyState label="אין נתוני שקילה השבוע" />
                ) : (
                  <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px", marginBottom: "10px" }}>
                    <StatCard
                      value={`${detail.weightAvg.toFixed(1)} ק"ג`}
                      valueColor={t.text}
                      label="ממוצע שבועי"
                      sub={detail.weightLastWeek != null ? `שבוע שעבר: ${detail.weightLastWeek.toFixed(1)} ק"ג` : null}
                    />
                    <StatCard
                      value={weightTrendStr(detail.weightTrend)}
                      valueColor={weightTrendColor(detail)}
                      label="מגמה שבועית"
                      sub={weightGoalLabel(detail)}
                      badge={weightGoalBadge(detail)}
                    />
                  </Box>
                )}

                {/* 📈 Weight chart */}
                <WeightChart detail={detail} traineeId={traineeId} weekOffset={internalWeekOffset} />

                {/* 👟 Steps */}
                <SectionHeader emoji="👟" label="צעדים — ממוצע שבועי" />
                {!(detail.stepsAvg != null || detail.stepsLastWeek != null) ? (
                  <EmptyState label="אין נתוני צעדים עדיין" />
                ) : (
                  <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px", marginBottom: "14px" }}>
                    <StatCard
                      value={detail.stepsAvg != null ? detail.stepsAvg.toLocaleString() : "–"}
                      valueColor={stepsAvgColor(detail)}
                      label="ממוצע שבועי"
                      sub={detail.stepsGoal ? `יעד: ${detail.stepsGoal.toLocaleString()}` : null}
                      badge={stepsGoalBadge(detail)}
                    />
                    <StatCard
                      value={detail.stepsLastWeek != null ? detail.stepsLastWeek.toLocaleString() : "–"}
                      valueColor={t.t2}
                      label="שבוע שעבר"
                      sub={detail.stepsGoal ? `יעד: ${detail.stepsGoal.toLocaleString()}` : null}
                      badge={stepsGoalBadgeLastWeek(detail)}
                    />
                  </Box>
                )}

                {/* 🔥 Calories */}
                <SectionHeader emoji="🔥" label={nutritionMode === POINTS ? "תזונה — ממוצע נקודות" : "תזונה — ממוצע קלורי"} />
                {!(detail.caloriesAvg != null || detail.caloriesLastWeek != null) ? (
                  <EmptyState label="אין יומן תזונה לשבוע זה" />
                ) : (
                  <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px", marginBottom: "14px" }}>
                    <StatCard
                      value={detail.caloriesAvg != null ? `${energyNum(detail.caloriesAvg)} ${energyLabel(nutritionMode)}` : "–"}
                      valueColor={t.text}
                      label="ממוצע שבועי"
                      sub={detail.caloriesGoal ? `יעד: ${energyNum(detail.caloriesGoal)}` : null}
                      badge={caloriesGoalBadge(detail)}
                    />
                    <StatCard
                      value={detail.caloriesLastWeek != null ? `${energyNum(detail.caloriesLastWeek)} ${energyLabel(nutritionMode)}` : "–"}
                      valueColor={t.t2}
                      label="שבוע שעבר"
                      sub={detail.caloriesGoal ? `יעד: ${energyNum(detail.caloriesGoal)}` : null}
                      badge={caloriesGoalBadgeLastWeek(detail)}
                    />
                  </Box>
                )}

                {/* 🏆 Personal Records */}
                <SectionHeader emoji="🏆" label="שיאים אישיים — משקלי עבודה" />
                {!(Array.isArray(detail.personalRecords) && detail.personalRecords.length > 0) ? (
                  <EmptyState label="אין שיאים חדשים בבלוק הנוכחי" />
                ) : (
                  <Box sx={{ display: "flex", flexDirection: "column", gap: "6px", marginBottom: "14px" }}>
                    {detail.personalRecords.map((pr, i) => (
                        <Box
                          key={i}
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: "10px",
                            padding: "9px 12px",
                            background: t.greenLight,
                            border: "1px solid rgba(1,187,167,.25)",
                            borderRadius: t.rs,
                          }}
                        >
                          <Box sx={{ flex: 1 }}>
                            <Box sx={{ fontSize: 13, fontWeight: 600 }}>{pr.exerciseNameHeb || pr.exerciseName}</Box>
                            <Box sx={{ fontSize: 10, fontWeight: 700, color: t.green, marginTop: "2px" }}>
                              שיא חדש!
                            </Box>
                          </Box>
                          <Box sx={{ display: "inline-flex", alignItems: "baseline", gap: "6px" }}>
                            <Box sx={{ fontSize: 15, fontWeight: 800, color: t.green }}>{pr.newBest}</Box>
                            <Box component="span" sx={{ fontSize: 12, color: t.t3 }}>←</Box>
                            <Box sx={{ fontSize: 12, fontWeight: 600, color: t.t3 }}>{pr.previousBest}</Box>
                            <Box component="span" sx={{ fontSize: 10, color: t.t3 }}>ק״ג</Box>
                          </Box>
                        </Box>
                      ))}
                  </Box>
                )}

                {/* ⚠️ Stalled */}
                <SectionHeader emoji="⚠️" label="דעיכה / תקיעה עקבית בביצועים" />
                {!(Array.isArray(detail.stalled) && detail.stalled.length > 0) ? (
                  <EmptyState label="אין תרגילים תקועים — יופי!" />
                ) : (
                  <Box sx={{ display: "flex", flexDirection: "column", gap: "5px", marginBottom: "14px" }}>
                    {detail.stalled.map((s, i) => {
                        const isDown = s.trend === "down";
                        return (
                          <Box
                            key={i}
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: "10px",
                              padding: "9px 12px",
                              background: isDown ? t.redLight : t.bg,
                              border: `1px solid ${isDown ? "rgba(224,69,69,.15)" : t.border}`,
                              borderRadius: t.rs,
                            }}
                          >
                            <Box sx={{ flex: 1 }}>
                              <Box sx={{ fontSize: 12.5, fontWeight: 600 }}>{s.exerciseNameHeb}</Box>
                              <Box sx={{ fontSize: 11, marginTop: "2px" }}>
                                <Box component="span" sx={{ color: isDown ? t.redText : t.text, fontWeight: 700 }}>
                                  עכשיו: {s.currentMax} ק״ג
                                </Box>
                                <Box component="span" sx={{ color: t.t3 }}>
                                  {" "}← לפני {s.consecutiveWeeks} שב׳: {s.previousMax} ק״ג
                                </Box>
                              </Box>
                            </Box>
                            <Box sx={{
                              fontSize: 10, fontWeight: 700,
                              color: isDown ? t.redText : t.t2,
                              background: isDown ? t.redLight : t.bg,
                              padding: "2px 8px", borderRadius: "10px",
                              border: `1px solid ${isDown ? "rgba(224,69,69,.2)" : t.border}`,
                              whiteSpace: "nowrap",
                            }}>
                              {isDown ? `ירידה ${s.consecutiveWeeks} שב׳ רצוף` : `תקוע ${s.consecutiveWeeks} שב׳`}
                            </Box>
                          </Box>
                        );
                      })}
                  </Box>
                )}

                <Box sx={{ display: "flex", gap: "8px", marginTop: "18px" }}>
                  <Box
                    component="button"
                    onClick={() => onSendPush?.(detail)}
                    sx={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: "5px",
                      padding: "9px 16px",
                      borderRadius: t.rs,
                      background: t.green,
                      color: "#fff",
                      border: "none",
                      fontSize: 12.5,
                      fontWeight: 600,
                      cursor: "pointer",
                      fontFamily: "inherit",
                      "&:hover": { background: t.greenDark },
                    }}
                  >
                    <SendIcon sx={{ fontSize: 14 }} /> שלח הודעה
                  </Box>
                  <Box
                    component="button"
                    onClick={() => { onClose(); navigate(`/app/clients/${traineeId}`); }}
                    sx={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: "5px",
                      padding: "9px 14px",
                      borderRadius: t.rs,
                      background: t.bg,
                      color: t.text,
                      border: `1.5px solid ${t.border}`,
                      fontSize: 12.5,
                      fontWeight: 500,
                      cursor: "pointer",
                      fontFamily: "inherit",
                      "&:hover": { background: t.border },
                    }}
                  >
                    <EditIcon sx={{ fontSize: 14 }} /> פתח כרטיס מתאמן
                  </Box>
                </Box>

                <Box sx={{ fontSize: 10.5, fontWeight: 700, color: t.t3, textTransform: "uppercase", letterSpacing: ".08em", margin: "20px 0 8px", display: "flex", alignItems: "center", gap: "6px" }}>
                  הערות
                  <Box sx={{ flex: 1, height: "1px", background: t.border }} />
                </Box>

                {notes.length > 0 && (
                  <Box sx={{ display: "flex", flexDirection: "column", gap: "6px", marginBottom: "10px", maxHeight: 140, overflowY: "auto" }}>
                    {notes.map((n) => (
                      <Box
                        key={n.id}
                        sx={{
                          padding: "8px 10px",
                          background: t.bg,
                          border: `1px solid ${t.borderLight}`,
                          borderRadius: t.rxs,
                          fontSize: 12.5,
                          color: t.text,
                        }}
                      >
                        {n.message}
                      </Box>
                    ))}
                  </Box>
                )}

                <TextField
                  fullWidth
                  multiline
                  minRows={2}
                  value={noteDraft}
                  onChange={(e) => setNoteDraft(e.target.value)}
                  placeholder="כתוב הערה על הלקוח..."
                  inputProps={{ dir: "rtl" }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: t.rs,
                      background: t.bg,
                      fontSize: 12.5,
                      "& fieldset": { borderColor: t.border },
                    },
                    marginBottom: "8px",
                  }}
                />
                <Box
                  component="button"
                  onClick={onSave}
                  disabled={!noteDraft.trim() || savingNote}
                  sx={{
                    width: "100%",
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "5px",
                    padding: "9px",
                    borderRadius: t.rs,
                    background: savedTick ? t.greenDark : t.green,
                    color: "#fff",
                    border: "none",
                    fontSize: 12.5,
                    fontWeight: 600,
                    cursor: !noteDraft.trim() || savingNote ? "not-allowed" : "pointer",
                    opacity: !noteDraft.trim() ? 0.6 : 1,
                    fontFamily: "inherit",
                    "&:hover": { background: t.greenDark },
                  }}
                >
                  {savedTick ? <CheckIcon sx={{ fontSize: 14 }} /> : null}
                  {savedTick ? "נשמר!" : "שמור הערה"}
                </Box>
              </>
            )}
          </DialogContent>
        </Box>
      </RTL>
    </Dialog>
  );
};

export default TraineeProfileDialog;
